import React, {useMemo} from "react";
import {Box, Stack, Tooltip, Typography} from "@mui/material";
import {calculateBasicFigures, StatFigure, StatRanges, VisitStats} from "./OfficeVisitStats";
import {useTagSx} from "../tag/Tag";
import {OfficeVisit} from "../../api/Model";
import {WeekDay} from "../../util/DateUtil";
import {getOrSet} from "../../util/MapUtil";
import {emptyArray} from "../../util/FunctionUtil";
import {grey} from "@mui/material/colors";

export function VisitStatBlock({color = 'inherit', children, tooltip, tooltipWidth, onClick}: {
    color?: 'error' | 'warning' | 'inherit',
    tooltip?: React.ReactNode,
    tooltipWidth?: number,
    onClick?: () => void,
    children: React.ReactNode,
}) {
    const tagSx = useTagSx(color, !!onClick)
    return tooltip ? (
        <Tooltip title={tooltip} slotProps={{tooltip: {sx: {minWidth: tooltipWidth}}}}>
            <Typography component="div" typography="body2" onClick={onClick} sx={tagSx}>{children}</Typography>
        </Tooltip>
    ) : (
        <Typography component="div" typography="body2" onClick={onClick} sx={tagSx}>{children}</Typography>
    );
}

type VisitStatsFigureField = {
    [K in keyof VisitStats]: VisitStats[K] extends StatFigure ? K : never
}[keyof VisitStats];

const display: Record<VisitStatsFigureField, { label: string, description: string }> = {
    avgVisits: {
        label: 'Average',
        description: 'Average number of occupied desks per day',
    },
    median: {
        label: 'Median',
        description: 'Median number of occupied desks per day',
    },
    percentile90: {
        label: 'P₉₀',
        description: 'P₉₀ percentile of occupied desks per day',
    },
    maxVisits: {
        label: 'Visits max',
        description: 'Maximum number of occupied desks per day',
    },
};

function figurePresentation(figure: StatFigure): string {
    return `${figure.value}${Number.isFinite(figure.percent) ? ` (${figure.percent}%)` : ""}`;
}

function FigureTemperature({minPossibleValue, maxPossibleValue, minValue, maxValue, value, color}: {
    minPossibleValue: number,
    maxPossibleValue: number,
    minValue: number | undefined,
    maxValue: number | undefined,
    value: number,
    color: 'inherit' | 'error' | 'warning',
}) {
    const tColor = color !== 'inherit' ? 'white' : grey[500];
    const possibleRange = maxPossibleValue - minPossibleValue;

    if (minValue === undefined || maxValue === undefined || possibleRange <= 0 || minValue > maxValue) {
        return null;
    }

    const max = Math.max(maxValue, maxPossibleValue, value);
    const min = Math.min(minValue, minPossibleValue, value);
    const absoluteLength = Math.max(max - min, 1);
    const rangeLeftPct = (minValue - min) * 100 / absoluteLength;
    const rangeWidthPct = (maxValue - minValue) * 100 / absoluteLength;
    const valuePct = (value - min) * 100 / absoluteLength;

    return (
        <Box component="span" sx={{position: 'relative', width: '100%', height: '5px', display: 'block'}}>
            {/* Baseline from minPossibleValue to maxPossibleValue */}
            <Box component="span" sx={{
                position: 'absolute',
                top: '2px',
                height: '1px',
                left: 0,
                width: '100%',
                backgroundColor: tColor,
            }}/>
            {/* Thick line from minValue to maxValue */}
            <Box component="span" sx={{
                position: 'absolute',
                top: '1px',
                height: '3px',
                left: `${rangeLeftPct}%`,
                width: `${rangeWidthPct}%`,
                borderRadius: 1,
                backgroundColor: tColor,
            }}/>
            {/* Value tick */}
            <Box component="span" sx={{
                position: 'absolute',
                top: '0px',
                height: '5px',
                left: `calc(${valuePct}% - 1px)`,
                width: '3px',
                backgroundColor: tColor,
                boxShadow: `0 0 0 1px ${tColor}`,
            }}/>
        </Box>
    );
}

export function FigureVisitStatBlock({stats, statRanges, field}: {
    stats: VisitStats,
    statRanges?: StatRanges,
    field: VisitStatsFigureField,
}) {
    const figure = stats[field];
    const range = statRanges?.[field];
    const percent = figure.percent;
    const paletteColor = percent <= 100
        ? percent > 85
            ? 'warning'
            : 'inherit'
        : 'error';

    return (
        <VisitStatBlock
            color={paletteColor}
            tooltip={
                <Stack direction="column" gap={1}>
                    <Typography variant="body2">
                        {display[field].description}
                    </Typography>
                    <Typography variant="body2">
                        This neighbourhood: {figurePresentation(figure)}
                    </Typography>
                    {range?.min !== undefined && (
                        <Typography variant="body2">
                            Office min: {range.min}%
                        </Typography>
                    )}
                    {range?.max !== undefined && (
                        <Typography variant="body2">
                            Office max: {range.max}%
                        </Typography>
                    )}
                </Stack>
            }
        >
            <Stack component="span" direction="column">
                {display[field].label}: {figurePresentation(figure)}
                {statRanges && (
                    <FigureTemperature
                        minPossibleValue={0}
                        maxPossibleValue={100}
                        minValue={statRanges[field].min}
                        maxValue={statRanges[field].max}
                        value={stats[field].percent}
                        color={paletteColor}
                    />
                )}
            </Stack>
        </VisitStatBlock>
    );
}

export function OverflowDaysStatBlock({stats, statRanges}: {
    stats: VisitStats,
    statRanges?: StatRanges,
}) {
    const paletteColor = stats.overflowDays > 0 ? 'error' : 'inherit';
    return (
        <VisitStatBlock
            color={paletteColor}
            tooltip={
                <Stack direction="column" gap={1}>
                    <Typography variant="body2">
                        Number of days when employees visiting the office exceed available desks.
                    </Typography>
                    <Typography variant="body2">
                        This neighbourhood: {stats.overflowDays}
                    </Typography>
                    {statRanges?.overflowDays?.min !== undefined && (
                        <Typography variant="body2">
                            Office min: {statRanges.overflowDays.min}
                        </Typography>
                    )}
                    {statRanges?.overflowDays?.max !== undefined && (
                        <Typography variant="body2">
                            Office max: {statRanges.overflowDays.max}
                        </Typography>
                    )}
                </Stack>
            }
        >
            <Stack component="span" direction="column">
                Overflow days: {stats.overflowDays}
                {statRanges && (
                    <FigureTemperature
                        minPossibleValue={0}
                        maxPossibleValue={statRanges.overflowDays.max || 2}
                        minValue={statRanges.overflowDays.min}
                        maxValue={statRanges.overflowDays.max}
                        value={stats.overflowDays}
                        color={paletteColor}
                    />
                )}
            </Stack>
        </VisitStatBlock>
    );
}

function Bars({columns}: {
    columns: { label: string, value: number, weekend: boolean }[]
}) {
    const heightPx = 16;
    const heights = useMemo(() => {
        const maxValue = Math.max(1, ...columns.map(v => v.value));
        return columns.map(column => ({
            label: column.label,
            value: column.value,
            weekend: column.weekend,
            height: Math.max(1, column.value / maxValue * heightPx),
        }));
    }, [columns]);

    return (
        <span style={{display: 'inline-flex', alignItems: 'flex-end', height: heightPx, gap: 4, marginLeft: 6}}>
            {heights.map(column => (
                <span key={column.label}
                      style={{
                          display: 'inline-block',
                          width: 6,
                          height: column.height,
                          background: column.weekend ? '#F06292' : 'currentColor',
                          opacity: 0.85,
                          borderRadius: 1,
                      }}
                      title={`${column.label}: ${column.value}`}
                />
            ))}
        </span>
    );
}

const dayColumns = [
    {label: 'Mon', day: WeekDay.Monday, weekend: false},
    {label: 'Tue', day: WeekDay.Tuesday, weekend: false},
    {label: 'Wed', day: WeekDay.Wednesday, weekend: false},
    {label: 'Thu', day: WeekDay.Thursday, weekend: false},
    {label: 'Fri', day: WeekDay.Friday, weekend: false},
    {label: 'Sat', day: WeekDay.Saturday, weekend: true},
    {label: 'Sun', day: WeekDay.Sunday, weekend: true},
];

export function WeekVisitStatBlock({visits}: { visits: OfficeVisit[] }) {
    const weeklyVisits = useMemo(() => {
        const weekDayVisits = new Map<number, number[]>();
        visits.forEach(visit => getOrSet(weekDayVisits, new Date(visit.date).getDay(), emptyArray).push(visit.visitorCount));
        const stats = dayColumns.map(({day, label, weekend}) => ({
            stats: calculateBasicFigures(weekDayVisits.get(day) || []),
            label,
            weekend,
        }));

        return {
            avgValues: stats.map(it => ({label: it.label, value: it.stats.avg, weekend: it.weekend})),
            maxValues: stats.map(it => ({label: it.label, value: it.stats.max, weekend: it.weekend})),
            medianValues: stats.map(it => ({label: it.label, value: it.stats.median, weekend: it.weekend})),
            p90Values: stats.map(it => ({label: it.label, value: it.stats.p90, weekend: it.weekend})),
        };
    }, [visits]);

    const tooltip = (
        <Stack gap={1} direction="column">
            <Stack direction="row" gap={1} alignItems="baseline" justifyContent="space-between">
                <Typography variant="body1">Max</Typography>
                <Bars columns={weeklyVisits.maxValues}/>
            </Stack>
            <Stack direction="row" gap={1} alignItems="baseline" justifyContent="space-between">
                <Typography variant="body1">Average</Typography>
                <Bars columns={weeklyVisits.avgValues}/>
            </Stack>
            <Stack direction="row" gap={1} alignItems="baseline" justifyContent="space-between">
                <Typography variant="body1">Median</Typography>
                <Bars columns={weeklyVisits.medianValues}/>
            </Stack>
            <Stack direction="row" gap={1} alignItems="baseline" justifyContent="space-between">
                <Typography variant="body1">P₉₀</Typography>
                <Bars columns={weeklyVisits.p90Values}/>
            </Stack>
        </Stack>
    );

    return (
        <VisitStatBlock tooltip={tooltip}>
            Week:
            <Bars columns={weeklyVisits.avgValues}/>
        </VisitStatBlock>
    );
}
